import pRetry from 'p-retry';
import { useAdditionalInfosResource } from '../use-additional-infos-event-resource';
import { createEventController } from './create-event-controller';
import { AdditionalInfosController } from './types';

export interface CreateScheduledEventControllerConfig {
    entityId: number;
    resource: ReturnType<typeof useAdditionalInfosResource>;
    pageSize: number;
}

/**
 * Scheduled event controller.
 *
 * Overrides loadPage (different endpoint, always hasNextPage: false) and
 * createFilterDirective (() => true). Delegates createInfo, deleteInfo, and
 * createStreamListeners to the event controller — scheduled events use the
 * same CRUD APIs and the same 'infos' WebSocket stream.
 */
export function createScheduledEventController(
    config: CreateScheduledEventControllerConfig
): AdditionalInfosController {
    const { entityId, resource, pageSize } = config;

    const eventController = createEventController({
        entityId,
        resource,
        pageSize,
        includeEventNarratives: true,
    });

    return {
        async loadPage() {
            return pRetry(
                async () => {
                    const response = await resource.getScheduledEventInfos({
                        agencyEventId: entityId,
                    });
                    const data = response.data.data;
                    return {
                        additionalInfos: data?.additionalInfos ?? [],
                        pagination: { hasNextPage: false },
                        agencyEventNumberToIdMap: data?.agencyEventNumberToIdMap ?? {},
                    };
                },
                { retries: 2 }
            );
        },

        createInfo: eventController.createInfo,
        deleteInfo: eventController.deleteInfo,
        createStreamListeners: eventController.createStreamListeners,

        createFilterDirective() {
            return () => true;
        },
    };
}
